import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { getUsageAnalytics } from '../firebase';
import { BarChart3, Users, Activity, AlertCircle, TrendingUp, Calendar, Download } from 'lucide-react';
import AccessDenied from '../components/AccessDenied';

const AdminAnalytics = () => {
  const { user, isSuperAdmin } = useUser();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  const [filterType, setFilterType] = useState('');

  // Check if user is super admin
  if (!isSuperAdmin()) {
    return <AccessDenied user={user} reason="This page is only accessible to super administrators." />;
  }

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading analytics with params:', {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        type: filterType || undefined,
        limit: 5000
      });
      
      const result = await getUsageAnalytics({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        type: filterType || undefined,
        limit: 5000
      });
      
      console.log('Analytics response:', result);
      console.log('Analytics data:', result.data);
      
      if (result && result.data) {
        if (result.data.success) {
          setAnalytics(result.data);
          console.log('Analytics set successfully:', {
            logsCount: result.data.logs?.length || 0,
            summary: result.data.summary
          });
        } else {
          console.error('Analytics request failed:', result.data.error);
          setError(result.data.error || 'Failed to load analytics');
        }
      } else {
        console.error('Unexpected response structure:', result);
        setError('Unexpected response format from server');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        details: err.details
      });
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [dateRange.startDate, dateRange.endDate, filterType]);

  const exportToCSV = () => {
    if (!analytics || !analytics.logs) return;

    const headers = ['Timestamp', 'Type', 'User', 'Page', 'Action', 'Function', 'Success', 'Duration', 'Error'];
    const rows = analytics.logs.map(log => [
      log.timestamp || '',
      log.type || '',
      log.userEmail || '',
      log.page || '',
      log.action || '',
      log.function || '',
      log.success !== undefined ? log.success : '',
      log.duration || '',
      log.error || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Activity className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          <p className="font-semibold">Error loading analytics</p>
        </div>
        <p className="text-red-600 mt-2">{error}</p>
        <button
          onClick={loadAnalytics}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const { summary, logs } = analytics || {};

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="text-gray-600 mt-1">Application utilization and performance metrics</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          <Download className="h-4 w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              <option value="page_view">Page Views</option>
              <option value="user_action">User Actions</option>
              <option value="function_call">Function Calls</option>
              <option value="error">Errors</option>
              <option value="performance">Performance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Logs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalLogs.toLocaleString()}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-primary-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Page Views</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.pageViews.toLocaleString()}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Function Calls</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.functionCalls.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Errors</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.errors.toLocaleString()}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>
      )}

      {/* Statistics by Type */}
      {summary && summary.byType && Object.keys(summary.byType).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Logs by Type</h2>
          <div className="space-y-2">
            {Object.entries(summary.byType)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-gray-700 capitalize">{type.replace('_', ' ')}</span>
                  <span className="font-semibold text-gray-900">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Top Users */}
      {summary && summary.byUser && Object.keys(summary.byUser).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Top Users</span>
          </h2>
          <div className="space-y-2">
            {Object.entries(summary.byUser)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([email, count]) => (
                <div key={email} className="flex items-center justify-between">
                  <span className="text-gray-700">{email}</span>
                  <span className="font-semibold text-gray-900">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Top Pages */}
      {summary && summary.byPage && Object.keys(summary.byPage).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Pages</h2>
          <div className="space-y-2">
            {Object.entries(summary.byPage)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([page, count]) => (
                <div key={page} className="flex items-center justify-between">
                  <span className="text-gray-700">{page}</span>
                  <span className="font-semibold text-gray-900">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {summary && summary.avgFunctionDuration && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Metrics</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Average Function Duration</span>
              <span className="font-semibold text-gray-900">{summary.avgFunctionDuration.toFixed(2)} ms</span>
            </div>
          </div>
        </div>
      )}

      {/* Recent Logs Table */}
      {logs && logs.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Logs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.slice(0, 100).map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        log.type === 'error' ? 'bg-red-100 text-red-800' :
                        log.type === 'function_call' ? 'bg-blue-100 text-blue-800' :
                        log.type === 'page_view' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {log.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.userEmail || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {log.page && <span className="mr-2">Page: {log.page}</span>}
                      {log.action && <span className="mr-2">Action: {log.action}</span>}
                      {log.function && <span className="mr-2">Function: {log.function}</span>}
                      {log.error && <span className="text-red-600">Error: {log.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;

